import React, { Fragment, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import styled from 'styled-components';
import { Container, Loader } from 'semantic-ui-react';
import { ThreeColumn } from 'components/templates';
import { HeaderPageWrapper } from 'components/templates/HeaderPageWrapper/HeaderPageWrapper';
import { BlogTopSavedPosts } from 'components/molecules/BlogTopSavedPosts/BlogTopSavedPosts';
import { FeedCard } from 'components/molecules/FeedCard/FeedCard';
import { Footer } from 'components/molecules/Footer';
import { SocialIconsGroup } from 'components/molecules';
import { Button, FeedPlaceholder, ButtonEnums, OnlyMobileCSS, OnlyDesktopCSS } from 'components/atoms';
import { SocialIconType } from '../../../constants';
import { isMobileViewPort, arrayIncludes, getPath } from 'utils';
import { TrendingArticles } from 'components/molecules/TrendingArticles/TrendingArticles';
import { Routes } from 'constants/index';
import { Route } from 'react-router-dom';

interface Props {
  contentBlogsLoading: boolean;
  allBlogsLoaded: boolean;
  contentBlogs: State.FeedCardContentItem[];
  savedBlogPosts: string[] | null;
  topSavedBlogPosts: Partial<State.ContentItem>[] | null;
  isAuth: boolean | null;
  copy: (copiedItem: State.CopiedItem) => void;
  getContentBlogs(options: { useMerge: boolean }): void;
  getDynamicTopTrendingBlog(): void;
  trendingBlogs: State.ITrendingArticle[] | null;
}

export const PageBlogFeed: React.FC<Props> = props => {
  const {
    contentBlogsLoading = false,
    allBlogsLoaded = false,
    contentBlogs,
    savedBlogPosts,
    topSavedBlogPosts,
    isAuth,
    copy,
    getContentBlogs,
    getDynamicTopTrendingBlog,
    trendingBlogs,
  } = props;
  const injectTrendingAfter: number = 4;
  const outerContainer: any = React.useRef();

  const getContentBlogsWithMerge = React.useCallback(() => getContentBlogs({ useMerge: true }), [getContentBlogs]);

  const [loadMoreIfScrolledBottom, cancel] = useDebouncedCallback(
    () => {
      if (isMobileViewPort()) return;
      if (contentBlogsLoading || allBlogsLoaded) return;
      if (!outerContainer || !outerContainer.current) return;
      const { scrollHeight, offsetHeight, scrollTop } = outerContainer.current;
      const remainingScroll = scrollHeight - offsetHeight - scrollTop;
      if (remainingScroll < 100) getContentBlogsWithMerge();
    },
    500,
    { maxWait: 2000 }
  );

  useEffect(() => {
    getDynamicTopTrendingBlog();
  }, [getDynamicTopTrendingBlog]);

  useEffect(() => {
    getContentBlogs({ useMerge: false });
    return cancel;
  }, [getContentBlogs, cancel]);

  const checkIfSaved = React.useCallback(
    (item: State.ContentItem) => {
      return arrayIncludes(savedBlogPosts, item.id);
    },
    [savedBlogPosts]
  );

  const renderFeedBottomSection = (): React.ReactNode => {
    return (
      <StyledBottomSectionContainer>
        {contentBlogsLoading && (
          <OnlyDesktopCSS as={Loader} active content="Loading more content..." inline size="small" />
        )}
        {!allBlogsLoaded && (
          <OnlyMobileCSS
            style={{}}
            as={Button}
            variant={ButtonEnums.variants.primary}
            loading={contentBlogsLoading}
            testId="home-feed-load-more-button"
            onClick={getContentBlogsWithMerge}
          >
            Show me more
          </OnlyMobileCSS>
        )}
      </StyledBottomSectionContainer>
    );
  };

  const renderContentFeed = (): React.ReactNode => {
    const filteredContent = contentBlogs.filter(contentItem => contentItem.contentfulType !== 'staticArticle');
    if (contentBlogsLoading && !filteredContent.length) {
      return <FeedPlaceholder />;
    }

    if (!filteredContent.length) {
      return (
        <>
          <h5>Blog Feed is empty</h5>
          <OnlyMobileCSS as={TrendingArticles} items={trendingBlogs} />
        </>
      );
    }

    return (
      <div>
        {filteredContent.map((item: State.FeedCardContentItem, i) => {
          const isContentSaved = checkIfSaved(item);
          return (
            <Fragment key={item.id}>
              <FeedCard contentItem={item} isContentSaved={isContentSaved} onCopy={copy} />
              {i === injectTrendingAfter && <OnlyMobileCSS as={TrendingArticles} items={trendingBlogs} />}
            </Fragment>
          );
        })}
        {renderFeedBottomSection()}
      </div>
    );
  };

  const renderPage = (): React.ReactNode => {
    return (
      <HeaderPageWrapper contentRef={outerContainer} contentOnScroll={loadMoreIfScrolledBottom} isFeedPage>
        <MainContainer>
          <ThreeColumn>
            <>
              <BlogTopSavedPosts isAuth={Boolean(isAuth)} posts={topSavedBlogPosts} />
            </>
            <>{renderContentFeed()}</>
            <>
              {!!contentBlogs.length && contentBlogs.length < injectTrendingAfter && (
                <OnlyMobileCSS as={TrendingArticles} items={trendingBlogs} />
              )}
              <StyledOnlyDesktopCSS>
                <TrendingArticles items={trendingBlogs} />
              </StyledOnlyDesktopCSS>
              <SocialIconsGroup
                title="Connect With Us"
                testId="blog-feed-connect-with-us"
                icons={[
                  SocialIconType.FACEBOOK,
                  SocialIconType.TWITTER,
                  SocialIconType.LINKEDIN,
                  SocialIconType.INSTAGRAM,
                ]}
              />
            </>
          </ThreeColumn>
        </MainContainer>
        <Route path={getPath(Routes.HELP)} component={Footer} />
      </HeaderPageWrapper>
    );
  };

  return <StyledContainer>{renderPage()}</StyledContainer>;
};

const MainContainer = styled(Container)`
  margin-top: ${props => props.theme.pxToRem(20)};
`;

const StyledBottomSectionContainer = styled.div`
  min-height: ${props => props.theme.pxToRem(70)};
  margin: ${props => props.theme.pxToRem(40)} 0;
  color: ${props => props.theme.colors.neutralGrey8};
  font-size: ${props => props.theme.fontSizes.l};
  text-align: center;
  display: flex;
  justify-content: center;

  ${props => props.theme.mediaQueries.mobileOnly} {
    min-height: ${props => props.theme.pxToRem(40)};
  }
`;

const StyledContainer = styled.div`
  ${props => props.theme.mediaQueries.desktopOnly} {
    height: ${props => props.theme.pxToRem(1)};
    min-height: 100%;
  }
`;

const StyledOnlyDesktopCSS = styled(OnlyDesktopCSS)`
  ${props => props.theme.mediaQueries.ieOnly} {
    display: inline !important;
  }
`;
